import type { ReactNode } from 'react'
import type { SessionDetail } from '~/gql/sessions/sessions'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Card, CardHeader } from '~/components/ui/card'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import SessionStatusBadge from '~/pages/backoffice/admin/sessions/components/SessionStatusBadge'

type IProps = {
  session: Pick<SessionDetail, 'name' | 'status' | 'startDate' | 'endDate' | 'tags'>
  timeZone?: string | null
  /** Actions de l'en-tête : « Modifier » pour qui peut gérer les parcours. */
  actions?: ReactNode
}

/** Nom, statut, dates et étiquettes de la session (#243). */
function SessionHeader({ session, timeZone, actions }: IProps) {
  const { t, i18n } = useTranslation()
  const date = (value: unknown) => formatSessionDate(value, i18n.language, timeZone, DATE_WITH_YEAR)
  const start = date(session.startDate)
  const end = date(session.endDate)
  const dates = start && end
    ? t('sessionDetail.dates.range', { start, end })
    : start
      ? t('sessionDetail.dates.from', { start })
      : end
        ? t('sessionDetail.dates.until', { end })
        : t('sessionDetail.dates.none')

  return (
    <Card {...tourAnchor('sessionDetail.header')}>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-semibold tracking-tight">{session.name}</h2>
            <SessionStatusBadge status={session.status} />
          </div>
          <p className="text-sm text-muted-foreground">{dates}</p>
          {session.tags.length > 0 && (
            <ul className="flex flex-wrap gap-1" aria-label={t('sessionDetail.tags')}>
              {session.tags.map(tag => (
                <li key={tag.id}><Badge variant="outline" className="font-normal">{tag.name}</Badge></li>
              ))}
            </ul>
          )}
        </div>
        {actions}
      </CardHeader>
    </Card>
  )
}

export default SessionHeader
